import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
export type LegalSection = {
  heading: string;
  /** Paragraphs of body copy. Render as <p> blocks. */
  body?: string[];
  /** Optional bullet list rendered under the body. */
  bullets?: string[];
};
type LegalPageProps = {
  /** Document title, e.g. "Refund & Return Policy". */
  title: string;
  /** One-line summary shown under the title. */
  intro: string;
  /** Human-readable "Last updated" date. */
  lastUpdated: string;
  sections: LegalSection[];
};
/**
 * Shared layout for the site's static legal pages (Refund, Privacy, Terms).
 * Mirrors the page chrome used across the site (CompareMaterials et al.):
 * top padding to clear the sticky navbar, warm light surface / dark surface,
 * brand-charcoal headings and brand-muted body copy.
 */
export function LegalPage({
  title,
  intro,
  lastUpdated,
  sections
}: LegalPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="pt-24 pb-20 bg-[#f8f7f6] dark:bg-[#2b2b2b] min-h-screen" data-id="element-3018" data-mp="1hg9d0fmnk2wx">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3019" data-mp="1met0wlkt3orx">
        <Link to="/" className="inline-flex items-center text-brand-muted hover:text-brand-orange transition-colors mb-6 font-bold text-sm" data-id="element-3020" data-mp="1mc6qme6j6v60">
          <ArrowLeft className="w-4 h-4 mr-1" data-id="element-3021" data-mp="1dcthxnq8eamc" />
          Back to Home
        </Link>

        <header className="mb-10 border-b border-stone-200 dark:border-white/10 pb-8" data-id="element-3022" data-mp="yoyna1snxw6m">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-3023" data-mp="13ff29qc4tpv3">
            {title}
          </h1>
          <p className="text-brand-muted dark:text-stone-300 mt-3 font-medium text-lg" data-id="element-3024" data-mp="pplaaz5byph5">
            {intro}
          </p>
          <p className="text-xs font-bold uppercase tracking-wider text-brand-muted dark:text-stone-400 mt-4" data-id="element-3025" data-mp="tn9aaglvr8fw">
            Last updated: {lastUpdated}
          </p>
        </header>

        <div className="space-y-10" data-id="element-3026" data-mp="ko5q19az8mzt">
          {sections.map(section => <section key={section.heading} data-id="element-3027" data-mp="2med24kx3p5pp">
              <h2 className="font-heading text-xl md:text-2xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-3028" data-mp="2mg7m9x0jstsq">
                {section.heading}
              </h2>
              {section.body?.map((paragraph, i) => <p key={i} className="text-brand-muted dark:text-stone-300 font-medium leading-relaxed mb-3 last:mb-0" data-id="element-3029" data-mp="2djf08obz6im8">
                  {paragraph}
                </p>)}
              {section.bullets && <ul className="mt-3 space-y-2 list-disc pl-5 marker:text-brand-orange" data-id="element-3030" data-mp="35auw78zf9hp4">
                  {section.bullets.map((bullet, i) => <li key={i} className="text-brand-muted dark:text-stone-300 font-medium leading-relaxed" data-id="element-3031" data-mp="2wc0tr1zetx3n" data-mpi={i}>
                      {bullet}
                    </li>)}
                </ul>}
            </section>)}
        </div>

        <div className="mt-12 rounded-2xl border border-stone-200 dark:border-white/10 bg-white dark:bg-white/[0.04] p-6" data-id="element-3032" data-mp="1kxdtjccjb90e">
          <h2 className="font-heading text-lg font-extrabold text-brand-charcoal dark:text-white mb-2" data-id="element-3033" data-mp="1fx995b8i9u4w">
            Questions?
          </h2>
          <p className="text-brand-muted dark:text-stone-300 font-medium leading-relaxed" data-id="element-3034" data-mp="1at6w9lhrsws9">
            Reach our team at{' '}
            <a href="tel:206-619-3742" className="text-brand-orange font-bold hover:underline" data-id="element-3035" data-mp="1fs0ajg4vsrth">
              (206) 619-3742
            </a>{' '}
            or{' '}
            <a href="mailto:info@206gravel.com" className="text-brand-orange font-bold hover:underline" data-id="element-3036" data-mp="1kkti0brqie99">
              info@206gravel.com
            </a>
            .
          </p>
        </div>
      </div>
    </main>;
}